/**
 * Formatos impresos de Grupo Ángeles (GA) para Medtronic, generados con pdfmake.
 *
 * Réplica de los formatos en papel (GA_Folio.pdf, GA-Bitacoras.pdf): mismas
 * secciones, colores y orden. Donde el formato trae la lista completa del
 * catálogo (códigos desechables), aquí solo va lo que se capturó en el sistema.
 *
 * Funciones puras: reciben los datos ya resueltos (nombres, no ids) y regresan
 * la definición del documento. La descarga la hace GaPdfService.
 */

// ── Paleta tomada de los formatos ─────────────────────────────────────────
export const GA = {
  blue: '#1010E6',      // barras de encabezado / encabezados de tabla
  navy: '#0B1F66',      // marcos y líneas de firma
  label: '#1F3C88',     // etiquetas
  line: '#1F4E8C',      // líneas de captura
  grid: '#8FA3C9',      // líneas de tabla
  ses: '#A3159A',       // SES (magenta)
  emid: '#1010E6',      // EMID (azul)
  sti: '#F26A1B',       // STI (naranja)
  otros: '#333333',     // Otros (negro)
  green: '#2DB34A',
  white: '#FFFFFF',
};

export interface GaItem { cot: string; code: string; description: string; batch: string; pieces: number; }

export interface GaFolioData {
  group: string; date: string; folio: string;
  surgeon: string; patient: string; nurse: string; technician: string; surgery: string;
  startHour: string; finishHour: string; hospital: string; service: string;
  cir: string; episode: string; tray: string; tower: string; head: string; lens: string; soni: string; operationRoom: string;
  items: GaItem[];
  observations: string[];
}

const PAGE_PORTRAIT = { width: 540, height: 720 };
const PAGE_LANDSCAPE = { width: 720, height: 540 };
const v = (x: any) => (x === null || x === undefined || String(x).trim() === '') ? '' : String(x);

/** Color de la columna COT según la línea de negocio. */
export function cotColor(cot: string): string {
  const c = String(cot ?? '').toUpperCase();
  if (c.startsWith('SES')) return GA.ses;
  if (c.startsWith('STI')) return GA.sti;
  if (c.startsWith('OTRO')) return GA.otros;
  if (c.startsWith('EMID')) return GA.emid;
  return GA.label;
}

/** "Etiqueta ________valor" con la línea de captura del formato. */
function field(label: string, value: string, labelW: number, lineW: number, opts: any = {}): any {
  return {
    columns: [
      { width: labelW, text: label, color: GA.label, fontSize: opts.fontSize ?? 8.5, alignment: opts.align ?? 'left', margin: [0, 3, 6, 0] },
      { width: lineW, stack: [
        { text: value || ' ', fontSize: 8.5, bold: true, color: '#111', margin: [2, 2, 0, 1], noWrap: false },
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: lineW, y2: 0, lineWidth: 1.2, lineColor: GA.line }] },
      ]},
    ],
    columnGap: 0, margin: [0, 0, 0, 3],
  };
}

function itemsTable(rows: GaItem[], widths: any[], minRows: number): any {
  const head = ['COT', 'Código', 'Descripción', 'Lote', 'Piezas'].map(t => ({
    text: t, bold: true, color: GA.white, fillColor: GA.blue, fontSize: 6.5, alignment: t === 'Piezas' ? 'center' : 'left',
  }));
  const body: any[] = [head];
  for (const r of rows) {
    body.push([
      { text: r.cot, bold: true, color: GA.white, fillColor: cotColor(r.cot), fontSize: 6, alignment: 'center' },
      { text: r.code, fontSize: 6.5, color: GA.label },
      { text: r.description, fontSize: 6.5, color: GA.label },
      { text: r.batch, fontSize: 6.5 },
      { text: String(r.pieces ?? ''), fontSize: 6.5, alignment: 'center' },
    ]);
  }
  // Renglones en blanco para completar a mano (como en el formato).
  for (let i = rows.length; i < minRows; i++) {
    body.push([{ text: ' ', fillColor: '#3A3A3A', fontSize: 6 }, { text: ' ', fontSize: 6.5 }, '', '', '']);
  }
  return {
    table: { headerRows: 1, widths, body },
    layout: {
      hLineWidth: () => 0.5, vLineWidth: () => 0.5,
      hLineColor: () => GA.grid, vLineColor: () => GA.grid,
      paddingLeft: () => 2, paddingRight: () => 2, paddingTop: () => 1.2, paddingBottom: () => 1.2,
    },
  };
}

/** FOLIO DE CIRUGÍA (GA_Folio.pdf) */
export function buildFolioDoc(d: GaFolioData): any {
  const emid = d.items.filter(i => !/^(SES|STI|OTRO)/i.test(i.cot));
  const rest = d.items.filter(i => /^(SES|STI|OTRO)/i.test(i.cot));
  const obs = d.observations.filter(x => v(x));

  return {
    pageSize: PAGE_PORTRAIT,
    pageMargins: [20, 96, 20, 64],
    defaultStyle: { fontSize: 8, color: '#111' },
    info: { title: `Folio de cirugía ${d.folio}` },
    background: () => [
      { canvas: [
        { type: 'rect', x: 0, y: 0, w: 540, h: 38, color: GA.blue },
        { type: 'line', x1: 290, y1: 72, x2: 400, y2: 72, lineWidth: 1.5, lineColor: GA.navy },
        { type: 'rect', x: 440, y: 40, w: 98, h: 30, lineWidth: 1.5, lineColor: GA.navy },
        { type: 'rect', x: 4, y: 82, w: 532, h: 634, lineWidth: 2.5, lineColor: GA.navy },
      ]},
      { text: 'FOLIO DE CIRUGIA', absolutePosition: { x: 10, y: 12 }, color: GA.white, bold: true, fontSize: 12 },
      { text: 'Medtronic', absolutePosition: { x: 450, y: 8 }, color: GA.white, bold: true, fontSize: 17 },
      { text: d.group, absolutePosition: { x: 14, y: 52 }, bold: true, fontSize: 11 },
      { text: 'FECHA:', absolutePosition: { x: 252, y: 50 }, color: GA.label, fontSize: 10 },
      { text: d.date, absolutePosition: { x: 296, y: 56 }, bold: true, fontSize: 9 },
      { text: 'FOLIO:', absolutePosition: { x: 400, y: 50 }, color: GA.label, fontSize: 10 },
      { text: d.folio, absolutePosition: { x: 444, y: 49 }, bold: true, fontSize: 10, width: 90, alignment: 'center' },
    ],
    footer: () => ({
      margin: [14, 10, 14, 0],
      columns: ['Nombre y Firma Técnico', 'Nombre y Firma Cirujano', 'Nombre y Firma Enfermera', 'Nombre y Firma VoBo Hospital'].map(t => ({
        stack: [
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 112, y2: 0, lineWidth: 1, lineColor: GA.navy }] },
          { text: t, color: GA.label, fontSize: 7.5, margin: [0, 3, 0, 0] },
        ], alignment: 'center', width: '*',
      })),
      columnGap: 10,
    }),
    content: [
      // ── Datos generales ────────────────────────────────────────────────
      {
        columns: [
          { width: 330, stack: [
            field('Nombre del Cirujano', d.surgeon, 110, 210),
            field('Nombre del Paciente', d.patient, 110, 210),
            field('Enf. Instrum/Circ', d.nurse, 110, 210),
            field('Nombre del Técnico', d.technician, 110, 210),
            field('Cirugía', d.surgery, 110, 210),
          ]},
          { width: '*', stack: [
            field('Hora de Inicio', d.startHour, 80, 82),
            field('Hora Termino', d.finishHour, 80, 82),
            { text: ' ', fontSize: 4 },
            field('HOSPITAL', d.hospital, 52, 110),
          ]},
        ],
        columnGap: 14,
      },
      { text: ' ', fontSize: 4 },
      // ── Códigos + equipo / servicio ────────────────────────────────────
      {
        columns: [
          { width: 300, stack: [
            { text: 'Códigos Desechables:', bold: true, color: GA.blue, fontSize: 11, margin: [0, 0, 0, 3] },
            itemsTable(emid, [26, 58, 112, 52, 26], Math.max(emid.length + 4, 12)),
          ]},
          { width: '*', stack: [
            { columns: [
              { width: 70, text: 'Servicio:', bold: true, color: GA.blue, fontSize: 11 },
              { width: 112, table: { widths: [104], body: [[{ text: d.service || '—', color: GA.white, bold: true, fillColor: GA.blue, fontSize: 8 }]] },
                layout: { hLineColor: () => GA.navy, vLineColor: () => GA.navy } },
            ], margin: [0, 0, 0, 6] },
            field('CIR', d.cir, 74, 110, { align: 'right', fontSize: 9 }),
            field('Episodio', d.episode, 74, 110, { align: 'right', fontSize: 9 }),
            field('Charola', d.tray, 74, 110, { align: 'right', fontSize: 9 }),
            field('Torre', d.tower, 74, 110, { align: 'right', fontSize: 9 }),
            field('Cabezal (NS)', d.head, 74, 110, { align: 'right', fontSize: 8 }),
            field('Lente (modelo y NS)', d.lens, 74, 110, { align: 'right', fontSize: 7.5 }),
            field('Soni', d.soni, 74, 110, { align: 'right', fontSize: 9 }),
            field('Quirófano', d.operationRoom, 74, 110, { align: 'right', fontSize: 9 }),
            { text: ' ', fontSize: 4 },
            itemsTable(rest, [20, 38, 52, 28, 24], Math.max(rest.length + 3, 8)),
            { text: ' ', fontSize: 6 },
            { table: { widths: ['*'], body: [[{ stack: [
                { text: 'Observaciones:', color: GA.label, fontSize: 9, margin: [0, 0, 0, 3] },
                ...(obs.length ? obs.map(o => ({ text: o, fontSize: 7.5, margin: [0, 0, 0, 2] })) : [{ text: ' ', fontSize: 18 }]),
                { text: ' ', fontSize: 6 },
                { text: 'Con su firma aquí corrobora recibir USB', color: GA.label, fontSize: 8 },
              ], margin: [4, 3, 4, 3] }]] },
              layout: { hLineWidth: () => 2, vLineWidth: () => 2, hLineColor: () => GA.navy, vLineColor: () => GA.navy } },
          ]},
        ],
        columnGap: 12,
      },
    ],
  };
}

// ── BITÁCORA SONICISION (GA-Bitacoras.pdf, páginas 1-3) ───────────────────

export interface GaSonicisionUse { date: string; folio: string; technician: string; }
export interface GaSonicisionData {
  hospital: string; bitacora: string; registro: string;
  color: string; generator: string; batteries: string; charger: string; tray: string;
  uses: GaSonicisionUse[];
}

function coverPage(title: string, hospital: string, bitacora: string, navyBlock: boolean): any[] {
  const W = 720;
  return [
    { canvas: [
      { type: 'rect', x: 0, y: 0, w: W, h: 205, color: GA.blue },
      ...(navyBlock ? [{ type: 'rect', x: 450, y: 0, w: 270, h: 205, color: GA.navy }] : []),
      { type: 'rect', x: 0, y: 450, w: W, h: 90, color: GA.blue },
      { type: 'rect', x: 600, y: 218, w: 112, h: 34, lineWidth: 1.5, lineColor: GA.line },
      { type: 'line', x1: 230, y1: 346, x2: 660, y2: 346, lineWidth: 2.5, lineColor: GA.line },
    ], absolutePosition: { x: 0, y: 0 } },
    { text: 'Medtronic', absolutePosition: { x: 545, y: 70 }, color: GA.white, bold: true, fontSize: 34 },
    { text: title, absolutePosition: { x: 26, y: 170 }, color: GA.white, bold: true, fontSize: 15 },
    { text: 'BITACORA:', absolutePosition: { x: 540, y: 230 }, color: GA.line, fontSize: 8 },
    { text: bitacora, absolutePosition: { x: 604, y: 227 }, bold: true, fontSize: 11, width: 104, alignment: 'center' },
    { text: 'HOSPITAL:', absolutePosition: { x: 86, y: 318 }, color: GA.navy, fontSize: 20 },
    { text: hospital, absolutePosition: { x: 236, y: 322 }, bold: true, fontSize: 15 },
  ];
}

function usesTable(start: number, count: number, uses: GaSonicisionUse[], rowH = 12.9): any {
  const head = [
    { text: '', fillColor: GA.blue },
    { text: 'USO', color: GA.white, fillColor: GA.blue, fontSize: 8, alignment: 'center' },
    { text: 'FECHA', color: GA.white, fillColor: GA.blue, fontSize: 6, alignment: 'center' },
    { text: 'FOLIO CX', color: GA.white, fillColor: GA.blue, fontSize: 6, alignment: 'center' },
    { text: 'Nombre Técnico', color: GA.white, fillColor: GA.blue, fontSize: 6, alignment: 'center' },
  ];
  const body: any[] = [head];
  for (let n = start; n < start + count; n++) {
    const u = uses[n - 1];
    body.push([
      { text: 'SES', color: GA.white, fillColor: GA.ses, fontSize: 4.5, alignment: 'center' },
      { text: String(n), fontSize: 7, alignment: 'center' },
      { text: u?.date ?? '', fontSize: 6, alignment: 'center' },
      { text: u?.folio ?? '', fontSize: 6, alignment: 'center' },
      { text: u?.technician ?? '', fontSize: 5.5 },
    ]);
  }
  return {
    table: { headerRows: 1, widths: [14, 36, 50, 44, 60], heights: (i: number) => (i === 0 ? 16 : rowH), body },
    layout: {
      hLineWidth: () => 0.6, vLineWidth: () => 0.6, hLineColor: () => GA.line, vLineColor: () => GA.line,
      paddingTop: () => 1.5, paddingBottom: () => 0, paddingLeft: () => 1, paddingRight: () => 1,
    },
  };
}

export function buildSonicisionDoc(d: GaSonicisionData): any {
  const uses = d.uses;
  const pagesHeader = (title: string) => [
    { canvas: [{ type: 'rect', x: 0, y: 0, w: 720, h: 22, color: GA.blue }, { type: 'rect', x: 0, y: 530, w: 720, h: 10, color: GA.blue }],
      absolutePosition: { x: 0, y: 0 } },
    { text: title, absolutePosition: { x: 10, y: 6 }, color: GA.white, bold: true, fontSize: 9 },
    { text: 'Medtronic', absolutePosition: { x: 640, y: 4 }, color: GA.white, bold: true, fontSize: 11 },
  ];

  const content: any[] = [...coverPage('BITACORA SONICISION', d.hospital, d.bitacora, false), { text: '', pageBreak: 'after' }];

  // Página 2: datos del equipo + usos 1-100.
  content.push(...pagesHeader('BITACORA SONICISION'));
  content.push({
    absolutePosition: { x: 8, y: 32 },
    columns: [
      { width: 216, stack: [
        { columns: [
          { width: 70, text: 'REGISTRO:', color: GA.line, fontSize: 9, margin: [12, 12, 0, 0] },
          { width: 112, table: { widths: [108], heights: [22], body: [[{ text: d.registro, bold: true, alignment: 'center', margin: [0, 5, 0, 0] }]] },
            layout: { hLineWidth: () => 1.5, vLineWidth: () => 1.5, hLineColor: () => GA.line, vLineColor: () => GA.line } },
        ]},
        { text: ' ', fontSize: 20 },
        field('Equipo (color)', d.color, 70, 130, { fontSize: 7.5 }),
        field('GENERADOR', d.generator, 70, 130, { fontSize: 7.5 }),
        field('BATERIAS', d.batteries, 70, 130, { fontSize: 7.5 }),
        field('CARGADOR', d.charger, 70, 130, { fontSize: 7.5 }),
        field('CHAROLA', d.tray, 70, 130, { fontSize: 7.5 }),
        { text: ' ', fontSize: 22 },
        usesTable(1, 20, uses, 10),
      ]},
      { width: 220, stack: [usesTable(21, 40, uses, 9.4)] },
      { width: 220, stack: [usesTable(61, 40, uses, 9.4)] },
    ],
    columnGap: 6,
  });

  // Más usos (como la página 3 del formato: 101-140, 141-180, 181-200) y,
  // si pasa de 200, páginas de 120 usos (3 columnas de 40).
  const extraPage = (cols: [number, number][]) => {
    content.push({ text: '', pageBreak: 'before' });
    content.push(...pagesHeader('BITACORA SONICISION'));
    content.push({
      absolutePosition: { x: 8, y: 32 },
      columns: cols.map(([from, n]) => ({ width: 226, stack: [usesTable(from, n, uses, 9.4)] })),
      columnGap: 6,
    });
  };
  if (uses.length > 100) extraPage([[101, 40], [141, 40], [181, 20]]);
  for (let start = 201; start <= uses.length; start += 120) {
    extraPage([[start, 40], [start + 40, 40], [start + 80, 40]]);
  }

  return {
    pageSize: PAGE_LANDSCAPE,
    pageMargins: [0, 0, 0, 0],
    defaultStyle: { fontSize: 8, color: '#111' },
    info: { title: `Bitácora SONICISION ${d.generator}` },
    content,
  };
}
